/**
 * CARD PARTS (B8) — design-spec §C, parts 1–12. Composition only: every
 * surface, control and type style comes from "@/design".
 */
import { useEffect, useLayoutEffect, useMemo, useRef, type CSSProperties, type Ref } from "react";
import { Button, Fig, Icon, IconButton, Meta, Status, Whorl, cx, tokens } from "@/design";
import { STRIP_FONT_PX, STRIP_GAP_PX, STRIP_OVERHANG_PX, estimateStripText, layoutStripLabels } from "./format";
import type { CardView, FactView, StripGeometry } from "./view";
import { getProgress, getSequence, onSequence } from "./sequence";

type CSSVars = CSSProperties & { [k: `--${string}`]: string | number | undefined };

/* ── 1 · route strip ──────────────────────────────────────────────── */

let canvas2d: CanvasRenderingContext2D | null | undefined;
const widths = new Map<string, number>();

/**
 * Rendered width of a strip label: 600 weight Open Sans in caps with .12em tracking (the CSS of
 * .ld-loc-card-strip__name). Canvas text metrics, no layout. Cached once the face has loaded.
 */
function measureStripText(text: string, px: number): number {
  const key = `${px}|${text}`;
  const hit = widths.get(key);
  if (hit != null) return hit;
  if (canvas2d === undefined) canvas2d = typeof document !== "undefined" ? (document.createElement("canvas").getContext("2d") ?? null) : null;
  const caps = text.toUpperCase();
  if (!canvas2d) return (estimateStripText(text) * px) / STRIP_FONT_PX;
  canvas2d.font = `600 ${px}px ${tokens.type.text}`;
  const w = canvas2d.measureText(caps).width + caps.length * px * 0.12;
  let loaded: boolean;
  try {
    loaded = typeof document === "undefined" || !document.fonts || document.fonts.check(`600 ${px}px "Open Sans"`);
  } catch {
    loaded = true;
  }
  if (loaded) widths.set(key, w);
  return w;
}

/**
 * Whorl → proportional named segments → pin. The sun line is the drawn part:
 * full on first entry; during A → B it advances with route:progress (DOM write,
 * no React state). Names are laid out with their rendered widths (§C.1): the
 * full name, then the road's ref, then a word-boundary ellipsis; a name may use
 * its neighbours' unused room.
 */
export function RouteStrip({ view, live, geom }: { view: CardView; live: boolean; geom?: StripGeometry }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = view.place.id;
  const segs = view.strip?.segs;
  const trackPx = geom?.trackPx;
  const fontPx = geom?.fontPx ?? STRIP_FONT_PX;
  const labels = useMemo(() => {
    if (!segs) return [];
    if (!trackPx) return segs.map((s) => (s.label ? { text: s.label, x: null, w: null } : null));
    const k = fontPx / STRIP_FONT_PX;
    return layoutStripLabels(segs, {
      trackPx,
      measure: (t) => measureStripText(t, fontPx),
      gap: STRIP_GAP_PX * k,
      overhang: [STRIP_OVERHANG_PX[0] * k, STRIP_OVERHANG_PX[1] * k],
    });
  }, [segs, trackPx, fontPx]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const set = (p: number) => el.style.setProperty("--p", String(Math.max(0, Math.min(1, p))));
    if (!live) {
      set(1);
      return undefined;
    }
    const pr = getProgress();
    set(pr && pr.id === id ? pr.p : 0);
    return onSequence("progress", (e) => {
      if (e.id === id) set(e.p);
    });
  }, [live, id]);
  if (!view.strip) return null;
  const ticks = view.strip.ticks;
  return (
    <div ref={ref} className="ld-loc-card-strip" aria-hidden="true">
      <Whorl size={16} decorative className="ld-loc-card-strip__origin" />
      <div className="ld-loc-card-strip__track">
        <i className="ld-loc-card-strip__base" />
        <i className="ld-loc-card-strip__lit" />
        {ticks.map((t) => (
          <i key={t} className="ld-loc-card-strip__tick" style={{ left: `${t * 100}%` }} />
        ))}
        {view.strip.segs.map((s, i) => {
          const l = labels[i];
          if (!l) return null;
          // a measured label sits in its own box (2 px of slack against sub-pixel rounding); the estimate fills its segment
          const style: CSSProperties =
            l.x != null && l.w != null
              ? { left: `${l.x - 1}px`, width: `${l.w + 2}px` }
              : { left: `${s.from * 100}%`, width: `${(s.to - s.from) * 100}%` };
          return (
            <span key={`${s.from}:${s.name}`} className={cx("ld-loc-card-strip__name", l.x != null && "is-placed")} style={style}>
              {l.text}
            </span>
          );
        })}
      </div>
      <Icon name="mapPin" size={16} className="ld-loc-card-strip__pin" />
    </div>
  );
}

/* ── 2–4 · meta row, name, locality (the head, which cross-fades) ─── */

export function CardHead({
  view,
  current,
  live,
  strip,
  headingRef,
  className,
  style,
}: {
  view: CardView;
  /** The live head carries the heading id and focus target; the outgoing copy is hidden from AT. */
  current: boolean;
  live: boolean;
  /** The strip's track and type size for this layout (labels are laid out with rendered widths). */
  strip?: StripGeometry;
  headingRef?: Ref<HTMLHeadingElement>;
  className?: string;
  style?: CSSVars;
}) {
  return (
    <div className={cx("ld-loc-card-head", className)} style={style} aria-hidden={current ? undefined : true}>
      <RouteStrip view={view} live={live} geom={strip} />
      <div className="ld-loc-card-body ld-loc-card-headbody">
        <div className="ld-loc-card-row">
          <span className="ld-loc-card-cat">
            <Icon name={view.icon} size={14} />
            <Meta className="ld-loc-card-meta">{view.category?.label ?? ""}</Meta>
          </span>
          {view.status !== "existing" && <Status s={view.status} />}
        </div>
        <h3 ref={current ? headingRef : undefined} id={current ? "ld-card-title" : undefined} tabIndex={current ? -1 : undefined} className="ld-h3 ld-loc-card-name">
          {view.name}
        </h3>
        {view.locality && <p className="ld-loc-card-locality">{view.locality}</p>}
        {view.peek && <p className="ld-loc-card-peekline ld-num">{view.peek}<span> off-peak</span></p>}
      </div>
    </div>
  );
}

/* ── 5 · facts (a <dl>: label first in the DOM, shown under the figure) ── */

function Fact({ f, figRef }: { f: FactView; figRef?: Ref<HTMLElement> }) {
  if (f.key === "dir") {
    return (
      <div className="ld-loc-card-fact">
        <dt className="ld-fact-l">
          <span className="ld-sr">Direction: </span>
          <span aria-hidden="true">{f.label}</span>
        </dt>
        <dd>
          <Icon name="arrowUpRight" size={22} className="ld-loc-card-arrow" style={{ transform: `rotate(${f.rotate ?? 0}deg)` }} />
          <span className="ld-sr">{f.spoken}</span>
        </dd>
      </div>
    );
  }
  return (
    <div className="ld-loc-card-fact">
      <dt className="ld-fact-l">{f.label}</dt>
      <dd ref={figRef}>
        {/* a space only screen readers get, so the figure reads "9.8 km", not "9.8km" (§H.2) */}
        <Fig
          className="ld-loc-card-fig"
          value={
            f.unit ? (
              <>
                {f.value}
                <span className="ld-sr"> </span>
              </>
            ) : (
              f.value
            )
          }
          unit={f.unit}
        />
      </dd>
    </div>
  );
}

/* ── the turn list: reduced motion's drive (§E.6) ─────────────────── */

export function TurnList({ view, id, headingRef }: { view: CardView; id: string; headingRef?: Ref<HTMLHeadingElement> }) {
  return (
    <section className="ld-loc-card-turns" id={id} aria-labelledby={`${id}-title`}>
      <h4 id={`${id}-title`} ref={headingRef} tabIndex={-1} className="ld-meta">
        The route, road by road
      </h4>
      <ol>
        <li className="is-end">
          <i aria-hidden="true">
            <Whorl size={14} decorative />
          </i>
          <span>Ládera City</span>
          <span className="km" />
        </li>
        {view.turns.map((t, k) => (
          <li key={`${k}:${t.name}`}>
            <i aria-hidden="true" />
            <span>{t.name}</span>
            <span className="km">{t.km} km</span>
          </li>
        ))}
        <li className="is-end">
          <i aria-hidden="true">
            <Icon name="mapPin" size={16} />
          </i>
          <span>{view.name}</span>
          <span className="km" />
        </li>
      </ol>
      {view.total && (
        <p className="ld-loc-card-turns__total">
          <span>Total</span>
          <span>{view.total}</span>
        </p>
      )}
    </section>
  );
}

/* ── 5–10 · the body (swaps at the dock) ──────────────────────────── */

export function CardBody({
  view,
  landing,
  turnsOpen,
  turnsId,
  turnsRef,
  onExtra,
  onDockFig,
}: {
  view: CardView;
  landing: boolean;
  turnsOpen: boolean;
  turnsId: string;
  turnsRef?: Ref<HTMLHeadingElement>;
  onExtra?: () => void;
  /** Registers the km figure the route meter lands on (null + the old one to unregister). */
  onDockFig?: (el: HTMLElement | null, prev?: HTMLElement | null) => void;
}) {
  const kmRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = kmRef.current?.querySelector<HTMLElement>(".ld-fig") ?? null;
    if (!el || !onDockFig) return undefined;
    onDockFig(el);
    return () => onDockFig(null, el);
  }, [onDockFig, view.place.id]);
  const facts = view.facts;
  return (
    <>
      {facts.length > 0 && (
        <dl className={cx("ld-facts ld-loc-card-facts", landing && "is-landing")} style={facts.length !== 3 ? { gridTemplateColumns: `repeat(${facts.length},minmax(0,1fr))` } : undefined}>
          {facts.map((f) => (
            <Fact key={f.key} f={f} figRef={f.key === "km" ? kmRef : undefined} />
          ))}
        </dl>
      )}
      {view.numbersNote && <p className="ld-loc-card-numbers">{view.numbersNote}</p>}
      {view.via && (
        <p className="ld-loc-card-via">
          <Icon name="route" size={16} />
          <span>{view.via}</span>
        </p>
      )}
      {view.notes.length > 0 && <p className="ld-panel__note ld-loc-card-note">{view.notes.join(" ")}</p>}
      {view.siteNote && <p className="ld-panel__note ld-loc-card-note">{view.siteNote}</p>}
      {view.extra &&
        (onExtra ? (
          <button type="button" className="ld-lightnote ld-loc-card-extra" onClick={onExtra}>
            <Icon name="mapPin" size={18} />
            <span>{view.extra}</span>
          </button>
        ) : (
          <p className="ld-lightnote">
            <Icon name="mapPin" size={18} />
            <span>{view.extra}</span>
          </p>
        ))}
      {view.futureNote && (
        <p className="ld-lightnote">
          <Icon name="info" size={18} />
          <span>{view.futureNote}</span>
        </p>
      )}
      {turnsOpen && view.turns.length > 0 && <TurnList view={view} id={turnsId} headingRef={turnsRef} />}
    </>
  );
}

/* ── 11–12 · footer ───────────────────────────────────────────────── */

export function CardFoot({
  view,
  kiosk,
  reduced,
  turnsOpen,
  turnsId,
  pos,
  onDrive,
  onTurns,
  onShare,
  onStep,
}: {
  view: CardView;
  kiosk: boolean;
  reduced: boolean;
  turnsOpen: boolean;
  turnsId: string;
  pos: { i: number; n: number } | null;
  onDrive: () => void;
  onTurns: () => void;
  onShare: () => void;
  onStep: (dir: 1 | -1) => void;
}) {
  const share = !kiosk;
  const row1 = view.canDrive || share;
  const openMaps = () => {
    window.open(view.gmaps, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="ld-panel__foot ld-loc-card-foot">
      {row1 && (
        <div className="ld-loc-card-actions">
          {view.canDrive ? (
            reduced ? (
              <Button variant="sun" block icon="list" className="ld-loc-card-drive" aria-expanded={turnsOpen} aria-controls={turnsId} onClick={onTurns}>
                See the route, road by road
              </Button>
            ) : (
              <Button variant="sun" block icon="car" className="ld-loc-card-drive" onClick={onDrive}>
                Take the drive
              </Button>
            )
          ) : (
            share && (
              <Button variant="glass" block icon="share" className="ld-loc-card-drive" onClick={onShare}>
                Share this place
              </Button>
            )
          )}
          {/* the button sits at the card's right edge: its tooltip opens to the left, inside the card */}
          {view.canDrive && share && <IconButton icon="share" label="Share this place" size={44} tip="left" onClick={onShare} />}
        </div>
      )}
      <div className="ld-loc-card-nav">
        {!kiosk && (
          <Button variant="quiet" role="link" iconRight="external" className="ld-loc-card-gmaps" onClick={openMaps}>
            Open in Google Maps
          </Button>
        )}
        <span className="ld-loc-card-nav__gap" />
        {/* no dead controls: a place that is not in the list (the site) has no pager */}
        {pos && pos.i > 0 && (
          <>
            <IconButton icon="chevL" label="Previous place" size={kiosk ? 56 : 36} iconSize={kiosk ? 22 : 18} tip={kiosk ? null : "up"} onClick={() => onStep(-1)} disabled={pos.n < 2} />
            <Meta className="ld-loc-card-pos ld-num">{`${pos.i} of ${pos.n}`}</Meta>
            <IconButton icon="chevR" label="Next place" size={kiosk ? 56 : 36} iconSize={kiosk ? 22 : 18} tip={kiosk ? null : "up"} onClick={() => onStep(1)} disabled={pos.n < 2} />
          </>
        )}
      </div>
    </div>
  );
}

/* ── small hooks ──────────────────────────────────────────────────── */

/** Esc closes, unless someone above us (a menu, the layers card) took it. */
export function useEscape(active: boolean, fn: () => void): void {
  const f = useRef(fn);
  useEffect(() => {
    f.current = fn;
  });
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || e.isComposing) return;
      e.preventDefault();
      f.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);
}

/** Current sequence without subscribing (for event handlers). */
export const sequenceNow = getSequence;
